import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import Select, { components as ReactSelectComponents } from "react-select";
import _last from "lodash/last";
import _get from "lodash/get";
import styled from "styled-components";

import Control from "./AutocompleteControl";
import Option from "./Option";
import MultiValue from "./MultiValue";
import {
  NoOptionsMessage,
  Placeholder,
  ValueContainer,
  DropdownIndicator,
  IndicatorSeparator,
  Input,
  ClearIndicator,
} from "./components";
import {
  PORevisionContext,
  RevisionReferenceContext,
} from "../../../../withPORevision";
import { calculateReference } from "../../../../withPORevision/withRevisionReference";
import { escapeStringRegexp } from "../../../../utils/stringUtils";
import { getLabel, getNullAsEmptyValue } from "../StandardSelect";
import {
  getRequiredStyle,
  onInputChangeWrapper,
} from "../SingleAutoCompleteSelect/components";
import { LAZY_LOAD_SCROLL_THRESHOLD } from "../../../../constants/lazyLoad";

const StyledSelect = styled(Select)`
  ${props => getRequiredStyle(props)}
`;

const defaultComponents = {
  Control,
  MultiValue,
  Option,
  ValueContainer,
  IndicatorSeparator,
  DropdownIndicator,
  NoOptionsMessage,
  Input,
  ClearIndicator,
};

export const filterOption = labelProperty => (option, search) => {
  const label = getLabel(labelProperty, option.data);
  return new RegExp(escapeStringRegexp(search), "ig").test(label);
};

export const buildChangeHandler = (
  isAutocompleteMulti,
  submitData,
  name,
  onChange,
  valueProperty = "id",
  nullAsEmpty,
  handleChange
) => data => {
  const items = submitData ? data : data.map(item => item[valueProperty]);
  const lastItem = _last(items);
  const value = isAutocompleteMulti ? items : lastItem;
  const event = {
    type: "click",
    target: {
      name,
      value: getNullAsEmptyValue({ value: value || "", nullAsEmpty }),
    },
  };

  const omitOnChange = handleChange && handleChange(event);
  if (!omitOnChange) {
    onChange(event);
  }
};

const customOnBlur = (name, onBlur) => evt =>
  onBlur({ ...evt, target: { ...evt.target, name } });

const getValue = ({
  isAutocompleteMulti,
  isSubmittingData,
  value,
  options,
  valueProperty,
  optionsCache = {},
}) => {
  if (!value) return [];
  const arr = isAutocompleteMulti && Array.isArray(value) ? value : [value];

  if (isSubmittingData) {
    return arr;
  }
  // Resolve each selected id against the current options, falling back to a
  // cache of every option seen so far. With lazy load / server-side search the
  // options list is replaced by the filtered results, so a previously selected
  // item can be missing from it; without the cache it would resolve to
  // undefined and react-select would drop it on the next change (BW-7016).
  return arr.map(
    value =>
      options.find(option => option[valueProperty] === value) ||
      optionsCache[value]
  );
};

export const getInputLabelProps = (
  value,
  { label, error, helperText, textFieldProps = {} },
  wasUpdated
) => {
  return {
    label,
    error,
    helperText,
    wasUpdated,
    ...textFieldProps,
  };
};

export const getComponents = (label, customComponents) => ({
  ...defaultComponents,
  Placeholder: label ? () => "" : Placeholder,
  ...customComponents,
});

export const isOptionDisabled = option =>
  _get(option, "menuItemProps.disabled");

const useMenuOpen = (defaultOption, openCallback = () => {}) => {
  const [isOpen, setIsOpen] = useState(defaultOption);

  const handleMenuOpen = () => {
    if (!isOpen) {
      setIsOpen(true);
      openCallback("");
    }
  };

  const handleMenuClose = () => {
    setIsOpen(false);
  };
  return [handleMenuClose, handleMenuOpen, isOpen];
};

export const LazyMenuList = ({ onScrollNearBottom, children, ...props }) => {
  const handleScroll = ({ currentTarget }) => {
    const { scrollHeight, scrollTop, clientHeight } = currentTarget;
    if (scrollHeight - scrollTop - clientHeight < LAZY_LOAD_SCROLL_THRESHOLD) {
      onScrollNearBottom();
    }
  };
  return (
    <ReactSelectComponents.MenuList
      {...props}
      innerProps={{ ...props.innerProps, onScroll: handleScroll }}
    >
      {children}
    </ReactSelectComponents.MenuList>
  );
};

LazyMenuList.propTypes = {
  onScrollNearBottom: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
};

const findScrollableAncestor = el => {
  let node = el?.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = window.getComputedStyle(node);
    if (overflowY === "auto" || overflowY === "scroll") return node;
    node = node.parentElement;
  }
  return null;
};

const useScrollLock = selectRef => {
  const scrollLockRef = useRef(null);

  useEffect(() => {
    return () => {
      if (scrollLockRef.current) {
        scrollLockRef.current.style.overflowY = "";
      }
    };
  }, []);

  const lockScroll = useCallback(() => {
    if (scrollLockRef.current) return;
    const controlEl =
      selectRef.current?.select?.controlRef || selectRef.current?.controlRef;
    const target = findScrollableAncestor(controlEl);
    if (target) {
      scrollLockRef.current = target;
      target.style.overflowY = "hidden";
    }
  }, [selectRef]);

  const unlockScroll = useCallback(() => {
    if (scrollLockRef.current) {
      scrollLockRef.current.style.overflowY = "";
      scrollLockRef.current = null;
    }
  }, []);

  return { lockScroll, unlockScroll };
};

// Remember every option ever passed, keyed by its value, so selected items
// keep resolving after a lazy-load/search replaces the options list with
// filtered results that no longer include them (BW-7016).
const useOptionsCache = (options, valueProperty) => {
  const optionsCacheRef = useRef({});
  options.forEach(option => {
    const key = option?.[valueProperty];
    if (key != null) optionsCacheRef.current[key] = option;
  });
  return optionsCacheRef;
};

const useWasModified = (name, isAutocompleteMulti) => {
  const { isActive, isUpdated, hasModifiedChildrens } = useContext(
    PORevisionContext
  );
  const { reference } = useContext(RevisionReferenceContext);

  return useMemo(() => {
    if (!isActive) return false;
    if (isAutocompleteMulti) {
      return hasModifiedChildrens(
        calculateReference(reference, name).reference
      );
    }
    return isUpdated(reference, name);
  }, [
    isActive,
    isUpdated,
    hasModifiedChildrens,
    reference,
    name,
    isAutocompleteMulti,
  ]);
};

const useLazyMenuList = onScrollNearBottom => {
  const onScrollNearBottomRef = useRef(onScrollNearBottom);
  useEffect(() => {
    onScrollNearBottomRef.current = onScrollNearBottom;
  }, [onScrollNearBottom]);

  return useMemo(() => {
    if (!onScrollNearBottom) return undefined;
    const MenuListWithLazyLoad = menuListProps => (
      <LazyMenuList
        {...menuListProps}
        onScrollNearBottom={(...args) =>
          onScrollNearBottomRef.current?.(...args)
        }
      />
    );
    MenuListWithLazyLoad.displayName = "MenuListWithLazyLoad";
    return MenuListWithLazyLoad;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!onScrollNearBottom]);
};

export const AutocompleteSelect = ({
  onChange,
  value,
  valueProperty,
  labelProperty,
  onBlur,
  options: initialOptions,
  nullAsEmpty,
  onMenuOpen,
  onScrollNearBottom,
  components: customComponent = {},
  handleChange,
  ...props
}) => {
  const [inputValue, setInputValue] = useState();
  const selectRef = useRef(null);

  const [handleMenuClose, handleMenuOpen, isMenuOpen] = useMenuOpen(
    false,
    onMenuOpen
  );
  const { lockScroll, unlockScroll } = useScrollLock(selectRef);

  const wrappedMenuOpen = useCallback(() => {
    handleMenuOpen();
    lockScroll();
  }, [handleMenuOpen, lockScroll]);

  const wrappedMenuClose = useCallback(() => {
    handleMenuClose();
    unlockScroll();
  }, [handleMenuClose, unlockScroll]);

  // Pass the options through untouched: with an empty list react-select
  // renders its non-selectable NoOptionsMessage. Injecting a placeholder
  // option here (optionsWithEmptyValue) broke selects with a function
  // labelProperty / custom isOptionDisabled, showing an empty selectable
  // row when a search had no results (BW-6969).
  const options = initialOptions || [];

  const optionsCacheRef = useOptionsCache(options, valueProperty);
  const wasModified = useWasModified(props.name, props.isAutocompleteMulti);

  const parsedValue = getValue({
    isAutocompleteMulti: props.isAutocompleteMulti,
    isSubmittingData: props.submitData,
    value,
    options,
    valueProperty,
    optionsCache: optionsCacheRef.current,
  });

  const lazyMenuList = useLazyMenuList(onScrollNearBottom);

  const components = useMemo(
    () =>
      getComponents(props.label, {
        ...customComponent,
        ...(lazyMenuList && { MenuList: lazyMenuList }),
      }),
    [props.label, customComponent, lazyMenuList]
  );

  return (
    <StyledSelect
      ref={selectRef}
      options={options}
      components={components}
      isMulti={true}
      onMenuOpen={wrappedMenuOpen}
      onMenuClose={wrappedMenuClose}
      onChange={buildChangeHandler(
        props.isAutocompleteMulti,
        props.submitData,
        props.name,
        onChange,
        valueProperty,
        nullAsEmpty,
        handleChange
      )}
      onBlur={onBlur && customOnBlur(props.name, onBlur)}
      hideSelectedOptions={true}
      value={parsedValue}
      filterOption={filterOption(labelProperty)}
      getOptionValue={data => {
        return data[valueProperty];
      }}
      textFieldProps={getInputLabelProps(value, props, wasModified)}
      getOptionLabel={data => getLabel(labelProperty, data)}
      isOptionDisabled={isOptionDisabled}
      noOptionsMessage={() => "No Options"}
      menuPlacement="auto"
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={{ menuPortal: styles => ({ ...styles, zIndex: 99999999 }) }}
      {...props}
      inputValue={inputValue}
      // Never disable while the menu is open: react-select blurs and closes
      // the menu when a focused select becomes disabled, which broke lazy-load
      // searches mid-typing (BW-6969). While closed, keep disabling during
      // loads (BW-6600).
      isDisabled={props.isDisabled || (props.isLoading && !isMenuOpen)}
      onInputChange={onInputChangeWrapper(props.onInputChange, setInputValue)}
    />
  );
};

AutocompleteSelect.defaultProps = {
  labelProperty: "name",
  valueProperty: "id",
  submitData: false,
};

AutocompleteSelect.propTypes = {
  submitData: PropTypes.bool,
  isAutocompleteMulti: PropTypes.bool,
  valueProperty: PropTypes.string,
  labelProperty: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  name: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  nullAsEmpty: PropTypes.bool,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({}),
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.arrayOf(PropTypes.shape({})),
  ]),
};

export default AutocompleteSelect;
